import React, { useState } from 'react'
import { Formik, Form } from 'formik'
import { FormikControl } from '..'
import { NETLIFY_FUNCTIONS_PATH, getFacebookCookies } from '../../../utils'
import axios from 'axios'
import { initialValues, contactSchema } from './contact-form-helpers'
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    AttributionSurvey,
    ConsentMsg,
} from '../shared'
import { useScrollToAnchor } from '../../../hooks/useScrollToAnchor.ts'

export function ContactForm({ siteData }) {
    initialValues.location = siteData.currentMetro.stores[0].salesforceValue

    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false)
    // State to hold form values for use in attribution survey
    const [formValues, setFormValues] = useState(null)
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 })

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('contact-form-wrapper')
        helpers.setSubmitting(true)
        const { fbp, fbc } = getFacebookCookies()
        try {
            const formData = {
                ...values,
                updates: true,
                cloudamp__data__c: localStorage.getItem('cloudamp__data__c'),
                formType: 'contact',
                fbp,
                fbc,
            }

            // Store form values for use in attribution survey
            setFormValues(formData)

            await axios.post(`${NETLIFY_FUNCTIONS_PATH}form-handler`, formData)

            // Show attribution survey instead of immediate success
            setShowAttributionSurvey(true)

            helpers.setSubmitting(false)
            helpers.setStatus('Success')

            helpers.resetForm()
        } catch (error) {
            console.error('Form submission error:', error)
            helpers.setSubmitting(false)
            helpers.setStatus('Failed')
            setFormValues(null)
            setShowAttributionSurvey(false)
        }
    }

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false)
        setTimeout(() => {
            setFormValues(null)
        }, 10000) // Clear form values after 10 second
    }

    return (
        <div className="contact-form-wrapper">
            <Formik
                initialValues={initialValues}
                validationSchema={contactSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {formik => {
                    if (formik.isSubmitting) {
                        return <FormSubmitting />
                    }

                    // Handle error state
                    if (formik.status === 'Failed') {
                        return <FormFailed />
                    }

                    if (formValues) {
                        // At this point the form is submitted
                        if (showAttributionSurvey) {
                            return (
                                <AttributionSurvey
                                    formValues={formValues}
                                    onComplete={handleAttributionComplete}
                                />
                            )
                        }
                        return <FormSuccess />
                    }
                    return (
                        <Form
                            id="contact-form"
                            className="w-100 pb-4"
                            method="POST"
                        >
                            {!showAttributionSurvey &&
                                !formValues &&
                                !formik.isSubmitting && (
                                    <h2 className="milan-text-primary h3 fw-normal text-center">
                                        Or Send Us A Question
                                    </h2>
                                )}
                            <div className="row justify-content-center pt-md-2">
                                <div className="col-md-6 mb-3">
                                    <FormikControl
                                        control="input"
                                        name="first_name"
                                        placeholder="First Name*"
                                        type="text"
                                        role="input"
                                        aria-required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <FormikControl
                                        control="input"
                                        name="last_name"
                                        placeholder="Last Name*"
                                        type="text"
                                        role="input"
                                        aria-required
                                    />
                                </div>
                            </div>
                            <div className="row justify-content-center mx-auto gx-2">
                                <div className="col-md-6 mb-3">
                                    <FormikControl
                                        control="phoneInput"
                                        name="phone"
                                        placeholder="Phone Number*"
                                        type="tel"
                                        role="input"
                                        aria-required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <FormikControl
                                        control="emailInput"
                                        name="email"
                                        placeholder="Your Email*"
                                        role="input"
                                        aria-required
                                    />
                                </div>
                                <div className="mb-3">
                                    <FormikControl
                                        control="selectLocation"
                                        id="location"
                                        name="location"
                                        title="Location"
                                        label="Location"
                                        milanStores={siteData.milanStores}
                                    />
                                </div>
                            </div>
                            <div className="row justify-content-center overflow-hidden ">
                                <div className="mb-2">
                                    <FormikControl
                                        control="textarea"
                                        name="description"
                                        placeholder="Hello..."
                                        rows="3"
                                        role="input"
                                    />
                                </div>
                            </div>
                            <div className="row justify-content-center mb-3">
                                <div className="col-12 lh-1">
                                    <ConsentMsg />
                                </div>
                            </div>

                            <div
                                id="actions-btns"
                                className="row justify-content-center mt-4"
                            >
                                <div className="col-10 col-lg-6 text-center">
                                    <button
                                        className="milan-btn milan-red-btn w-100"
                                        type="submit"
                                        disabled={!formik.isValid}
                                    >
                                        {formik.isSubmitting
                                            ? 'Submitting...'
                                            : 'Submit *'}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
        </div>
    )
}

export default ContactForm
